import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User as UserIcon, Eye, EyeOff } from "lucide-react";
import { LOGO_URL } from "../constants";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const errFromApi = (e) => {
    try {
        const msg = e?.message || "";
        const idx = msg.indexOf("{");
        if (idx >= 0) {
            const j = JSON.parse(msg.slice(idx));
            if (typeof j.detail === "string") return j.detail;
            if (Array.isArray(j.detail)) return j.detail.map((x) => x.msg).join(" ");
        }
    } catch { /* ignore */ }
    return e?.message || "Something went wrong";
};

export default function Login() {
    const [tab, setTab] = useState("login"); // login | register | forgot
    return (
        <div className="min-h-screen bg-ink-900 text-white flex flex-col">
            <div className="px-6 lg:px-10 h-20 flex items-center">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="back-home">
                    <ArrowLeft className="w-4 h-4" /> Back home
                </Link>
            </div>
            <div className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 sm:p-10">
                        <div className="text-center">
                            <div className="bg-black rounded-2xl px-4 py-3 inline-flex mb-6">
                                <img src={LOGO_URL} alt="Blue Mountain Group" className="h-10 w-auto" />
                            </div>
                            <h1 className="font-display font-black text-3xl tracking-tight">
                                {tab === "login" ? "Welcome operator." : tab === "register" ? "Join the operation." : "Reset your password."}
                            </h1>
                            <p className="text-sm text-gray-400 mt-2">
                                {tab === "login" && "Sign in to access your training, certificates and community."}
                                {tab === "register" && "Create an account in 30 seconds."}
                                {tab === "forgot" && "We'll send a reset link if the email is registered."}
                            </p>
                        </div>

                        {tab !== "forgot" && (
                            <div className="mt-7 grid grid-cols-2 gap-1 p-1 bg-ink-800 rounded-full">
                                <button
                                    data-testid="tab-login"
                                    onClick={() => setTab("login")}
                                    className={`py-2 rounded-full text-sm font-semibold transition-colors ${tab === "login" ? "bg-white text-ink-900" : "text-gray-400 hover:text-white"}`}
                                >Sign in</button>
                                <button
                                    data-testid="tab-register"
                                    onClick={() => setTab("register")}
                                    className={`py-2 rounded-full text-sm font-semibold transition-colors ${tab === "register" ? "bg-white text-ink-900" : "text-gray-400 hover:text-white"}`}
                                >Create account</button>
                            </div>
                        )}

                        <div className="mt-6">
                            {tab === "login" && <LoginForm onForgot={() => setTab("forgot")} />}
                            {tab === "register" && <RegisterForm />}
                            {tab === "forgot" && <ForgotForm onBack={() => setTab("login")} />}
                        </div>

                        {tab !== "forgot" && (
                            <>
                                <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500">
                                    <span className="flex-1 h-px bg-white/5" /> or <span className="flex-1 h-px bg-white/5" />
                                </div>
                                <GoogleButton />
                            </>
                        )}

                        <p className="mt-6 text-xs text-gray-500 text-center">
                            By continuing you agree to our Terms and Privacy Policy.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function GoogleButton() {
    const handle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
        <button
            onClick={handle}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-full border border-white/10 hover:bg-white/5 text-sm font-semibold transition-all"
        >
            <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
                <path fill="#4285F4" d="M21.6 12.227c0-.709-.064-1.39-.182-2.046H12v3.873h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.892-1.742 2.982-4.305 2.982-7.355z"/>
                <path fill="#34A853" d="M12 22c2.7 0 4.964-.895 6.618-2.418L15.386 17.07c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.596-4.123H3.064v2.59A9.997 9.997 0 0 0 12 22z"/>
                <path fill="#FBBC05" d="M6.404 13.902a6.005 6.005 0 0 1 0-3.804V7.51H3.064a10.005 10.005 0 0 0 0 8.98l3.34-2.588z"/>
                <path fill="#EA4335" d="M12 5.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.064 7.51l3.34 2.588C7.19 7.736 9.395 5.977 12 5.977z"/>
            </svg>
            Continue with Google
        </button>
    );
}

const inputCls = "w-full bg-ink-800 border border-white/5 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-lime/40";
const submitCls = "w-full px-6 py-3 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed";

function LoginForm({ onForgot }) {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await api.post("/auth/login", { email, password });
            setUser(res.user);
            toast.success(`Welcome back, ${res.user.name}`);
            navigate("/dashboard");
        } catch (err) {
            toast.error(errFromApi(err));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
            </div>
            <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls + " pr-11"} />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
            <div className="flex justify-end">
                <button data-testid="forgot-link" type="button" onClick={onForgot} className="text-xs text-gray-400 hover:text-lime">Forgot password?</button>
            </div>
            <button data-testid="login-submit" type="submit" disabled={busy} className={submitCls}>
                {busy ? "Signing in…" : "Sign in"}
            </button>
        </form>
    );
}

function RegisterForm() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error("Password must be at least 8 characters");
        setBusy(true);
        try {
            const res = await api.post("/auth/register", { email, password, name });
            setUser(res.user);
            toast.success(`Welcome, ${res.user.name}!`);
            navigate("/dashboard");
        } catch (err) {
            toast.error(errFromApi(err));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="register-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
            </div>
            <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
            </div>
            <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="register-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 8 chars)" className={inputCls} />
            </div>
            <button data-testid="register-submit" type="submit" disabled={busy} className={submitCls}>
                {busy ? "Creating account…" : "Create account"}
            </button>
        </form>
    );
}

function ForgotForm({ onBack }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email });
            toast.success("If that email is registered we've sent a reset link.");
            setEmail("");
            onBack();
        } catch {
            toast.error("Something went wrong");
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className={inputCls} />
            </div>
            <button data-testid="forgot-submit" type="submit" disabled={busy} className={submitCls}>
                {busy ? "Sending…" : "Send reset link"}
            </button>
            <button type="button" onClick={onBack} className="w-full text-xs text-gray-400 hover:text-white">Back to sign in</button>
        </form>
    );
}
